import { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useSetAtom, useAtom } from 'jotai';
import { IReplyByParent } from '../../inter/detail';
import {
  Loading,
  DeleteConfirm as ListReplyDeleteConfirm,
} from '../../widgets';
import { notificationAtom, pageLoadingAtom } from '../../atoms/system';
import { popupReplyAtom } from '../../atoms/reply';
import { listLoadReviewAtom } from '../../atoms/review';
import { listLoadQuestionAtom } from '../../atoms/question';
import { IResponse, ActionType } from '../../types';
import produce from 'immer';
import { useImmer } from 'use-immer';
import api from '../../api';
import CreateReply from './Create.Reply';
import EditReply from './Edit.Reply';
import PopupItem from './Popup.Item.Reply';
import PopupParent from './Popup.Parent.Reply';
import cuid from 'cuid';
import _ from 'lodash';

const PopupReply = ({ page_type }: { page_type: string }): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const setListLoad = useSetAtom(
    page_type === 'review' ? listLoadReviewAtom : listLoadQuestionAtom,
  );
  const [loading, setLoading] = useImmer<boolean>(false);
  const [replyData, setReplyData] = useImmer<IReplyByParent | null>(null);
  const [showPopupRely, setShowPopupRely] = useAtom(popupReplyAtom);
  const [reload, setReload] = useImmer<{
    load: string;
    trigger: boolean;
  }>({
    load: cuid(),
    trigger: false,
  });
  useEffect(() => {
    if (showPopupRely.parent_id.length > 0 && showPopupRely.show) {
      (async () => {
        try {
          setLoading(true);
          const result = await api
            .get(`api/comments/${showPopupRely.parent_id}/replies`)
            .json<IResponse<IReplyByParent>>();
          setReplyData(result.data);
          setLoading(false);
        } catch (e) {
          console.log(e);
          setShowPopupRely(
            produce(draft => {
              draft.show = false;
              draft.parent_id = '';
              draft.reply_id = null;
            }),
          );
          notification({
            show: true,
            type: null,
            title: null,
          });
        }
      })();
    }
  }, [showPopupRely.parent_id, reload.load]);
  const [showDeleteItem, setShowDeleteItem] = useImmer<{
    show: boolean;
    comment_id: string;
  }>({
    show: false,
    comment_id: '',
  });
  const handleDeleteItem = async (comment_id: string) => {
    try {
      pageLoading(true);
      await api.post(`api/comments/${comment_id}/delete-reply-${page_type}`);
      setShowDeleteItem(draft => {
        draft.show = false;
        draft.comment_id = '';
      });
      setReload(draft => {
        draft.load = cuid();
        draft.trigger = true;
      });
      if (comment_id === showPopupRely.reply_id) {
        setShowPopupRely(
          produce(draft => {
            draft.reply_id = null;
          }),
        );
      }
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  const handleAction = (type: ActionType, item: { id: string }) => {
    if (type === ActionType.edit) {
      setShowPopupRely(
        produce(draft => {
          draft.reply_id = item.id;
        }),
      );
    } else if (type === ActionType.delete) {
      setShowDeleteItem(draft => {
        draft.show = true;
        draft.comment_id = item.id;
      });
    }
  };
  return (
    <>
      <Transition show={showPopupRely.show} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => {
            setShowPopupRely(
              produce(draft => {
                draft.show = false;
                draft.reply_id = null;
                draft.parent_id = '';
              }),
            );
            if (reload.trigger) {
              setListLoad(cuid());
              setReload(draft => {
                draft.trigger = false;
              });
            }
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 pt-20">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="p-6 w-full max-w-5xl transform overflow-hidden rounded-md bg-white shadow-xl transition-all">
                  <div className="flex space-x-10">
                    <div className="flex flex-1 basis-7/12">
                      <div className="w-full flex flex-col space-y-4">
                        <h3 className="text-md font-medium text-gray-900">
                          Trả lời cho&nbsp;
                          {page_type === 'review' ? 'đánh giá' : 'câu hỏi'}
                        </h3>
                        {loading || _.isNull(replyData) ? (
                          <div className="flex h-full items-center justify-center">
                            <Loading color="indigo-600" size={6} />
                          </div>
                        ) : (
                          <div className="flex flex-col space-y-4 flex-1">
                            <PopupParent
                              props={{
                                created_at: replyData.createdAt,
                                desc: replyData.desc,
                                customer: replyData.customer,
                              }}
                              target={showPopupRely.target}
                            />
                            {replyData.replies.length > 0 ? (
                              <div className="block space-y-4 pl-12">
                                {replyData.replies.map((item, key: number) => (
                                  <PopupItem
                                    active={
                                      showPopupRely.reply_id === item.id ||
                                      false
                                    }
                                    props={item}
                                    action={handleAction}
                                    key={key}
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className="flex-1 p-4 rounded-md">
                                <p className="flex h-full items-center justify-center text-xs text-gray-700">
                                  Chưa có câu trả lời nào cho đánh giá này
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-1 basis-5/12">
                      {_.isString(showPopupRely.reply_id) &&
                      showPopupRely.reply_id.length > 0 ? (
                        <EditReply actionReload={setReload} />
                      ) : (
                        <CreateReply
                          prefix_uri={
                            page_type === 'review' ? 'reviews' : 'questions'
                          }
                          parent_id={showPopupRely.parent_id}
                          target_id={
                            !_.isNull(replyData) ? replyData.pageTargetId : ''
                          }
                          actionReload={setReload}
                        />
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <ListReplyDeleteConfirm
        payload={showDeleteItem}
        modal={setShowDeleteItem}
        action={handleDeleteItem}
      />
    </>
  );
};

export default PopupReply;
